import React, { useState, useEffect, useRef, useCallback } from "react";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import Select from "react-select";
import { getData } from "../../../webService/webservice.js";
import { useStore } from "../../../Context/context.jsx";
import { getDateRange } from "../../../utils/get-DateRange.js";
import { exportDataToExcel } from "../../../utils/excel-Export.js";
import "../../../styles/Auth/profit-loss-report.css";

const REV_CATEGORIES = ["Services", "Products", "Retail", "Memberships"];
const EXP_CATEGORIES = ["Salaries", "Rent", "Utilities", "Inventory", "Marketing", "Maintenance", "Travel", "Food", "Equipment", "Other"];

const COLUMN_LABELS = {
    category: "Category",
    itemName: "Item Name",
    subCategory: "Sub-Category",
    amount: "Amount",
    tax: "Total Tax",
    netAmount: "Net Amount",
    percentage: "Percentage (%) of Total"
};

export default function ProfitLossReport() {
    const today = new Date().toISOString().split("T")[0];
    const { selectedStoreId } = useStore();
    const sessionToken = localStorage.getItem("Token");
    const role = localStorage.getItem("role");
    const isSuperAdmin = role === "SuperAdmin" || role === "SelfAdmin";

    // ── Tabs ──
    const [activeTab, setActiveTab] = useState("revenue-overview");

    // ── Date ──
    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(today);
    const [dateRange, setDateRange] = useState("Today");

    // ── Data ──
    const [data, setData] = useState([]);
    const [summary, setSummary] = useState({ totalRevenue: 0, totalExpenses: 0, totalPurchases: 0, grossProfit: 0, netProfit: 0, totalTax: 0 });
    const [loading, setLoading] = useState(false);
    const [storeList, setStoreList] = useState([]);

    // ── Pagination ──
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    // ── Filters ──
    const [filterRevenueCat, setFilterRevenueCat] = useState("");
    const [filterExpenseCat, setFilterExpenseCat] = useState("");
    const [filterStoreId, setFilterStoreId] = useState("");
    const [filterVendorName, setFilterVendorName] = useState("");
    const [filterInvoiceNo, setFilterInvoiceNo] = useState("");
    const [tableSearch, setTableSearch] = useState("");

    // ── Refs ──
    const columnConfigRef = useRef(null);

    // ── Column Config ──
    const [showColumnConfig, setShowColumnConfig] = useState(false);
    const [visibleColumns, setVisibleColumns] = useState(Object.keys(COLUMN_LABELS));

    // ── Outside Click Logic ──
    useEffect(() => {
        function handleClickOutside(event) {
            if (columnConfigRef.current && !columnConfigRef.current.contains(event.target)) {
                setShowColumnConfig(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // ── Fetch Stores ──
    useEffect(() => {
        const fetchStores = async () => {
            try {
                const res = await getData("stores/stores", { limit: 1000 }, sessionToken);
                if (res?.data) setStoreList(Array.isArray(res.data) ? res.data : (res.data.results || []));
            } catch (err) { console.error(err); }
        };
        fetchStores();
    }, [sessionToken]);

    const fetchReport = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                startDate,
                endDate,
                storeId: filterStoreId || selectedStoreId || "",
                revenueCategory: filterRevenueCat,
                expenseName: filterExpenseCat,
                vendorName: filterVendorName,
                invoiceNo: filterInvoiceNo,
                tab: activeTab,
                page,
                limit
            };

            const res = await getData("reports/reports/profit-loss", params, sessionToken);

            if (res?.status === 200 || res?.success) {
                setData(res.data?.data || []);
                setSummary(res.data?.summary || { totalRevenue: 0, totalExpenses: 0, grossProfit: 0, netProfit: 0, totalTax: 0 });
                setTotalItems(res.data?.pagination?.totalItems || 0);
                setTotalPages(res.data?.pagination?.totalPages || 1);
            } else {
                toast.error(res?.message || "Failed to fetch P&L report");
            }
        } catch (error) {
            console.error(error);
            toast.error("An error occurred while fetching data");
        } finally {
            setLoading(false)
        }
    }, [
        startDate,
        endDate,
        selectedStoreId,
        filterRevenueCat,
        filterExpenseCat,
        filterStoreId,
        filterVendorName,
        filterInvoiceNo,
        activeTab,
        page,
        limit,
        sessionToken
    ]);

    useEffect(() => {
        fetchReport();
    }, [fetchReport]);

    const handleDateRangeChange = (range) => {
        setDateRange(range);
        if (range === "Custom") return;
        const { startDate: s, endDate: e } = getDateRange(range);
        setStartDate(s);
        setEndDate(e);
    };


    const handleResetFilters = () => {
        setFilterRevenueCat("");
        setFilterExpenseCat("");
        setFilterStoreId("");
        setFilterVendorName("");
        setFilterInvoiceNo("");
        setPage(1);
    };

    const handleExport = () => {
        if (!data.length) return toast.info("No data to export");
        const fileName = `Profit_Loss_Report_${startDate}_to_${endDate}`;

        // Transform data for export
        const exportData = data.map(item => ({
            "Category": item.category,
            "Item Name": item.itemName,
            "Sub-Category": item.subCategory,
            "Amount": item.amount,
            "Tax": item.tax,
            "Net Amount": item.netAmount,
            "Percentage": `${item.percentage}%`
        }));

        exportDataToExcel(exportData, "Profit & Loss Statement", `${fileName}.xlsx`);
    };

    const toggleColumn = (key) => {
        setVisibleColumns(prev =>
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
        );
    };

    const selectStyles = {
        control: (base) => ({
            ...base,
            minHeight: '44px',
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            background: '#f8fafc',
            boxShadow: 'none',
            '&:hover': { borderColor: '#3c0008' }
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected ? '#3c0008' : state.isFocused ? '#f8fafc' : 'white',
            color: state.isSelected ? 'white' : '#1e293b',
            '&:active': { backgroundColor: '#3c0008' }
        })
    };

    return (
        <div className="product-layout">
            <div className="pl-report-wrapper">
                <div className="pl-report-container">
                    {/* ── Header ── */}
                    <div className="pl-header">
                        <div className="header-text">
                            <h1>Profit & Loss Report</h1>
                            <p>Track your salon's net income and profitability for the selected period.</p>
                        </div>
                    </div>

                    {/* ── Tabs ── */}
                    <div className="pl-tabs-row">
                        {[
                            { id: "revenue-overview", label: "Revenue Overview" },
                            { id: "detailed-statement", label: "Detailed Statement" },
                            { id: "yoy", label: "Year-over-Year" },
                            { id: "category-analysis", label: "Category Analysis" }
                        ].map(t => (
                            <button
                                key={t.id}
                                className={`pl-tab-btn ${activeTab === t.id ? "active" : ""}`}
                                onClick={() => setActiveTab(t.id)}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>

                    {/* ── Filter Panel ── */}
                    <div className="pl-filter-panel">
                        <div className="pl-filter-grid">
                            <div className="filter-item">
                                <label>Date Range</label>
                                <div className="date-range-picker-combined">
                                    <select
                                        className="filter-select date-range-select"
                                        value={dateRange}
                                        onChange={e => {
                                            const range = e.target.value;
                                            setDateRange(range);
                                            const { startDate: s, endDate: eD } = getDateRange(range, startDate, endDate);
                                            setStartDate(s);
                                            setEndDate(eD);
                                        }}
                                    >
                                        <option value="Today">Today</option>
                                        <option value="Yesterday">Yesterday</option>
                                        <option value="Last 7 Days">Last 7 Days</option>
                                        <option value="Last Month">Last Month</option>
                                        <option value="Current Month">Current Month</option>
                                        <option value="Current FY">Current Financial Year</option>
                                        <option value="Previous FY">Previous Financial Year</option>
                                        <option value="Custom">Custom Date</option>
                                    </select>
                                    {dateRange === "Custom" ? (
                                        <div className="custom-date-inputs">
                                            <input
                                                type="date"
                                                className="filter-input date-input"
                                                value={startDate}
                                                onChange={(e) => setStartDate(e.target.value)}
                                            />
                                            <span className="date-separator">to</span>
                                            <input
                                                type="date"
                                                className="filter-input date-input"
                                                value={endDate}
                                                onChange={(e) => setEndDate(e.target.value)}
                                            />
                                        </div>
                                    ) : (
                                        <div className="date-display-box">
                                            <Icon icon="mdi:calendar-outline" width="18" />
                                            <span>{startDate === endDate ? startDate : `${startDate} - ${endDate}`}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {isSuperAdmin && (
                                <div className="filter-item">
                                    <label>Branch</label>
                                    <select className="filter-select" value={filterStoreId} onChange={e => setFilterStoreId(e.target.value)}>
                                        <option value="">All Branches</option>
                                        {storeList.map(s => <option key={s._id} value={s._id}>{s.branchName || s.storeName}</option>)}
                                    </select>
                                </div>
                            )}

                            <div className="filter-item">
                                <label>Revenue Category</label>
                                <Select
                                    styles={selectStyles}
                                    options={REV_CATEGORIES.map(c => ({ value: c, label: c }))}
                                    value={filterRevenueCat ? { value: filterRevenueCat, label: filterRevenueCat } : null}
                                    onChange={s => setFilterRevenueCat(s?.value || "")}
                                    isClearable
                                    placeholder="Services, Products..."
                                />
                            </div>

                            <div className="filter-item">
                                <label>Expense Category</label>
                                <Select
                                    styles={selectStyles}
                                    options={EXP_CATEGORIES.map(c => ({ value: c, label: c }))}
                                    value={filterExpenseCat ? { value: filterExpenseCat, label: filterExpenseCat } : null}
                                    onChange={s => setFilterExpenseCat(s?.value || "")}
                                    isClearable
                                    placeholder="Salaries, Rent..."
                                />
                            </div>

                            {/* <div className="filter-item">
                                <label>Vendor Name</label>
                                <div className="search-wrapper">
                                    <input
                                        type="text"
                                        className="filter-input-search"
                                        placeholder="Search Name"
                                        value={filterVendorName}
                                        onChange={e => setFilterVendorName(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="filter-item">
                                <label>Search Invoice/Ref #</label>
                                <input
                                    type="text"
                                    className="filter-input"
                                    placeholder="Search Invoice/Ref #"
                                    value={filterInvoiceNo}
                                    onChange={e => setFilterInvoiceNo(e.target.value)}
                                />
                            </div> */}

                            <div className="filter-actions-combined">
                                <button className="btn-reset-filters" onClick={handleResetFilters}>Reset</button>
                            </div>
                        </div>
                    </div>

                    {/* ── Summary Cards ── */}
                    <div className="pl-summary-grid">
                        <div className="summary-card revenue">
                            <div className="card-icon"><Icon icon="mdi:trending-up" /></div>
                            <div className="card-info">
                                <span className="card-label">Total Revenue</span>
                                <span className="card-value1">{(summary.totalRevenue)}</span>
                            </div>
                        </div>
                        <div className="summary-card purchases">
                            <div className="card-icon"><Icon icon="mdi:cart-outline" /></div>
                            <div className="card-info">
                                <span className="card-label">Total Purchases</span>
                                <span className="card-value1">{(summary.totalPurchases)}</span>
                            </div>
                        </div>
                        <div className="summary-card expenses">
                            <div className="card-icon"><Icon icon="mdi:trending-down" /></div>
                            <div className="card-info">
                                <span className="card-label">Total Expenses</span>
                                <span className="card-value1">{(summary.totalExpenses)}</span>
                            </div>
                        </div>
                        <div className="summary-card gross-profit">
                            <div className="card-icon"><Icon icon="mdi:calculator" /></div>
                            <div className="card-info">
                                <span className="card-label">Gross Profit</span>
                                <span className="card-value1">{(summary.grossProfit)}</span>
                            </div>
                        </div>
                        <div className="summary-card net-profit">
                            <div className="card-icon"><Icon icon="mdi:finance" /></div>
                            <div className="card-info">
                                <span className="card-label">Net Profit/Loss</span>
                                <span className="card-value1">{(summary.netProfit)}</span>
                            </div>
                        </div>
                    </div>

                    {/* ── Table ── */}
                    <div className="pl-table-card">
                        <div className="table-header-row">
                            <h2>
                                {activeTab === "revenue-overview" ? "Revenue Summary" :
                                    activeTab === "detailed-statement" ? "Detailed P&L Statement" :
                                        activeTab === "yoy" ? "Year-over-Year Comparison" :
                                            "Category Analysis"}
                            </h2>
                            <div className="table-tools">
                                {/* <div className="search-wrapper">
                                    <Icon icon="mdi:magnify" className="search-icon" />
                                    <input
                                        type="text"
                                        placeholder="Search Statement..."
                                        className="table-search"
                                        value={tableSearch}
                                        onChange={e => setTableSearch(e.target.value)}
                                    />
                                </div> */}

                                <div className="column-config-wrapper" ref={columnConfigRef}>
                                    <button className="tool-btn" onClick={() => setShowColumnConfig(!showColumnConfig)}>
                                        <Icon icon="mdi:view-column-outline" /> Columns
                                    </button>
                                    {showColumnConfig && (
                                        <div className="column-config-dropdown">
                                            <div className="config-header">Toggle Columns</div>
                                            {Object.entries(COLUMN_LABELS).map(([key, label]) => (
                                                <label key={key} className="config-item">
                                                    <input
                                                        type="checkbox"
                                                        checked={visibleColumns.includes(key)}
                                                        onChange={() => toggleColumn(key)}
                                                    />
                                                    {label}
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <button className="tool-btn" onClick={handleExport}>
                                    <Icon icon="mdi:microsoft-excel" /> Export
                                </button>
                            </div>
                        </div>

                        <div className="pl-table-wrapper">
                            <table className="pl-table">
                                <thead>
                                    <tr>
                                        {Object.entries(COLUMN_LABELS).map(([key, label]) => (
                                            visibleColumns.includes(key) && <th key={key}>{label}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr><td colSpan={visibleColumns.length} style={{ textAlign: 'center', padding: '40px' }}>Loading...</td></tr>
                                    ) : data.length === 0 ? (
                                        <tr><td colSpan={visibleColumns.length} style={{ textAlign: 'center', padding: '40px' }}>No records found</td></tr>
                                    ) : (
                                        <>
                                            {data
                                                .filter(row =>
                                                    row.itemName.toLowerCase().includes(tableSearch.toLowerCase()) ||
                                                    row.subCategory.toLowerCase().includes(tableSearch.toLowerCase())
                                                )
                                                .map((row, idx) => (
                                                    <tr key={idx} className={
                                                        row.category === "Revenue" ? "row-revenue" :
                                                            row.category === "Purchases" ? "row-purchase" :
                                                                "row-expense"
                                                    }>
                                                        {visibleColumns.includes("category") && (
                                                            <td>
                                                                <div className="category-cell">
                                                                    <Icon icon={row.category === "Revenue" ? "mdi:trending-up" : "mdi:trending-down"}
                                                                        style={{ color: row.category === "Revenue" ? "#10b981" : "#ef4444" }} />
                                                                    {row.category}
                                                                </div>
                                                            </td>
                                                        )}
                                                        {visibleColumns.includes("itemName") && <td>{row.itemName}</td>}
                                                        {visibleColumns.includes("subCategory") && <td>{row.subCategory}</td>}
                                                        {visibleColumns.includes("amount") && <td>{(row.amount)}</td>}
                                                        {visibleColumns.includes("tax") && <td>{(row.tax)}</td>}
                                                        {visibleColumns.includes("netAmount") && <td>{(row.netAmount)}</td>}
                                                        {visibleColumns.includes("percentage") && (
                                                            <td>
                                                                <div className="percent-bar-wrapper">
                                                                    <span>{row.percentage}%</span>
                                                                    <div className="percent-bar">
                                                                        <div className="percent-fill" style={{ width: `${row.percentage}%`, background: row.category === "Revenue" ? "#10b981" : "#ef4444" }}></div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                        )}
                                                    </tr>
                                                ))}
                                            {/* Summary Rows */}
                                            <tr className="summary-row-total">
                                                <td colSpan={visibleColumns.indexOf("amount") > -1 ? visibleColumns.indexOf("amount") : 1}>Total Revenue</td>
                                                {visibleColumns.includes("amount") && <td>{(summary.totalRevenue)}</td>}
                                                {visibleColumns.includes("tax") && <td>{(summary.totalTax)}</td>}
                                                {visibleColumns.includes("netAmount") && <td>{(summary.totalRevenue - summary.totalTax)}</td>}
                                                {visibleColumns.includes("percentage") && <td></td>}
                                            </tr>
                                        </>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* ── Pagination Footer ── */}
                        <div className="table-pagination">
                            <div className="pagination-info">
                                Showing {(page - 1) * limit + 1} to {Math.min(page * limit, totalItems)} of {totalItems} entries
                            </div>
                            <div className="pagination-controls">
                                <button
                                    className="page-btn"
                                    disabled={page === 1}
                                    onClick={() => setPage(p => p - 1)}
                                >
                                    <Icon icon="mdi:chevron-left" />
                                </button>
                                {[...Array(totalPages)].map((_, i) => (
                                    <button
                                        key={i}
                                        className={`page-btn ${page === i + 1 ? "active" : ""}`}
                                        onClick={() => setPage(i + 1)}
                                    >
                                        {i + 1}
                                    </button>
                                ))}
                                <button
                                    className="page-btn"
                                    disabled={page === totalPages}
                                    onClick={() => setPage(p => p + 1)}
                                >
                                    <Icon icon="mdi:chevron-right" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

    );
}
